import { useEffect, useState } from 'react';
import { clientService } from '../services/clientService';
import { notifyError, notifySuccess } from '../utils/notifications';
import { validateField, validateForm, sanitizeFormData } from '../utils/validators';

export default function ClientInfo({ isOpen, onClose, clientData, workshopID }) {
    const [editableData, setEditableData] = useState({
        name: '',
        first_lastname: '',
        second_lastname: '',
        company_name: '',
        address: '',
        phone: '',
        second_phone: '',
        RFC: '',
        email: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (clientData) {
            setEditableData({
                name: clientData.name || '',
                first_lastname: clientData.first_lastname || '',
                second_lastname: clientData.second_lastname || '',
                company_name: clientData.company_name || '',
                address: clientData.address || '',
                phone: clientData.phone || '',
                second_phone: clientData.second_phone || '',
                RFC: clientData.RFC || '',
                email: clientData.email || ''
            });
        }
    }, [clientData]);

    const handleInputChange = (field, value) => {
        // ✅ Validar en tiempo real
        const validation = validateField(field, value);
        
        setEditableData(prev => ({
            ...prev,
            [field]: validation.sanitized || value
        }));
        
        // Limpiar error si se vuelve válido
        if (validation.valid && errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        } else if (!validation.valid) {
            setErrors(prev => ({
                ...prev,
                [field]: validation.error
            }));
        }
    };

    const handleSave = async () => {
        try {
            const customerID = clientData.customer_id || clientData.id;

            if (!customerID) {
                console.error('No se encontró customer_id en:', clientData);
                notifyError('Error: No se pudo identificar el cliente');
                return;
            }

            if (!workshopID) {
                notifyError('Error: No se pudo identificar el taller');
                return;
            }

            // ✅ Validar todos los campos antes de guardar
            const fieldsToValidate = ['name', 'first_lastname', 'phone', 'email', 'RFC'];
            const validation = validateForm(editableData, fieldsToValidate);
            
            if (!validation.valid) {
                setErrors(validation.errors);
                notifyError('Por favor corrige los errores en el formulario');
                return;
            }

            // ✅ Sanitizar datos antes de enviar
            const payload = sanitizeFormData(editableData, [
                'name', 'first_lastname', 'second_lastname', 'company_name',
                'address', 'phone', 'second_phone', 'RFC', 'email'
            ]);

            await clientService.updateClient(customerID, payload);
            notifySuccess('Cliente actualizado exitosamente');
            onClose(!isOpen);
        } catch (error) {
            console.error('Error al guardar cliente:', error);
            if (error.response?.status === 404) {
                notifyError('Cliente no encontrado');
            } else if (error.response?.status === 403) {
                notifyError('No tienes permiso para editar este cliente');
            } else {
                notifyError('Error al guardar los cambios');
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={() => { onClose(!isOpen) }}>
            <div className="modal-container modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Información del Cliente</h2>
                    <button className="close-button" onClick={() => { onClose(!isOpen) }}>✕</button>
                </div>

                <div className="modal-body">
                    {/* Información personal */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>Nombre(s) *</label>
                            <input
                                type="text"
                                value={editableData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder="Nombre completo..."
                                style={errors.name ? { borderColor: '#dc3545' } : {}}
                            />
                            {errors.name && <span style={{ color: '#dc3545', fontSize: '0.8em' }}>{errors.name}</span>}
                        </div>

                        <div className="form-group">
                            <label>Primer apellido *</label>
                            <input
                                type="text"
                                value={editableData.first_lastname}
                                onChange={(e) => handleInputChange('first_lastname', e.target.value)}
                                placeholder="Apellido paterno..."
                                style={errors.first_lastname ? { borderColor: '#dc3545' } : {}}
                            />
                            {errors.first_lastname && <span style={{ color: '#dc3545', fontSize: '0.8em' }}>{errors.first_lastname}</span>}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Segundo apellido</label>
                            <input
                                type="text"
                                value={editableData.second_lastname}
                                onChange={(e) => handleInputChange('second_lastname', e.target.value)}
                                placeholder="Apellido materno..."
                            />
                        </div>

                        <div className="form-group">
                            <label>Nombre de empresa</label>
                            <input
                                type="text"
                                value={editableData.company_name}
                                onChange={(e) => handleInputChange('company_name', e.target.value)}
                                placeholder="Razón social (opcional)..."
                            />
                        </div>
                    </div>

                    {/* Información de contacto */}
                    <div className="form-group">
                        <label>Dirección completa</label>
                        <textarea
                            value={editableData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            placeholder="Calle, número, colonia, ciudad..."
                            rows="2"
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Teléfono principal *</label>
                            <input
                                type="tel"
                                value={editableData.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                placeholder="10 dígitos..."
                                style={errors.phone ? { borderColor: '#dc3545' } : {}}
                            />
                            {errors.phone && <span style={{ color: '#dc3545', fontSize: '0.8em' }}>{errors.phone}</span>}
                        </div>

                        <div className="form-group">
                            <label>Teléfono secundario</label>
                            <input
                                type="tel"
                                value={editableData.second_phone}
                                onChange={(e) => handleInputChange('second_phone', e.target.value)}
                                placeholder="Opcional..."
                            />
                        </div>
                    </div>

                    {/* Información fiscal */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>RFC</label>
                            <input
                                type="text"
                                value={editableData.RFC}
                                onChange={(e) => handleInputChange('RFC', e.target.value)}
                                style={errors.RFC ? { borderColor: '#dc3545' } : {}}
                            />
                            {errors.RFC && <span style={{ color: '#dc3545', fontSize: '0.8em' }}>{errors.RFC}</span>}
                        </div>

                        <div className="form-group">
                            <label>Correo electrónico</label>
                            <input
                                type="email"
                                value={editableData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                placeholder="correo@ejemplo.com"
                                style={errors.email ? { borderColor: '#dc3545' } : {}}
                            />
                            {errors.email && <span style={{ color: '#dc3545', fontSize: '0.8em' }}>{errors.email}</span>}
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn-cancel" onClick={() => { onClose(!isOpen) }}>
                        Cerrar
                    </button>
                    <button className="btn-save" onClick={handleSave}>
                        Guardar cambios
                    </button>
                </div>
            </div>
        </div>
    );
}